import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Loader } from "components/Loader";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { TableSection } from "features/Research/v2/components/StockDetailPage/StockDetailPage.styles";
import { Text } from "style/v2/components/Typography";
import {
  Card,
  CardHeader,
  CardContent,
  TitleInfoIcon,
  TooltipText,
} from "./TopHoldings.styles";
import { columns } from "./columns";
import PropTypes from "prop-types";
import { getStockCompanyIcon } from "utils/index";
import { Table } from "components/Table";
import { Tooltip } from "components/Tooltip";
import AllHoldingsModal from "features/Explore/v2/Components/AllHoldingsModal";
import { useAllStrategyHoldings } from "features/Strategy/hook/useAllStrategyHoldings";

const TOP_HOLDINGS_TOOLTIP = (
  <>
    <TooltipText>
      The strategy&apos;s largest positions today, ranked by portfolio weight.
    </TooltipText>
    <TooltipText>
      Share price and industry reflect the latest available data for each
      holding.
    </TooltipText>
    <TooltipText>
      Weight percentages shown are only for the top 10 allocations.
    </TooltipText>
  </>
);

const TopHoldings = ({ data, isLoading, error, strategyId }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: allHoldingsData,
    isLoading: isAllHoldingsLoading,
    error: allHoldingsError,
  } = useAllStrategyHoldings(strategyId);

  const holdings = useMemo(
    () =>
      [...data]
        .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity))
        .map((holding, index) => ({
          id: index + 1,
          asset: {
            symbol: holding.name,
            name: holding.company_name || holding.name,
            imageUrl: getStockCompanyIcon(holding.name),
          },
          weight: holding.value,
          sharePrice: holding.share_price,
          industry: holding.sector,
        })),
    [data]
  );

  // A strategy can have no top_holdings but still hold assets, and this card
  // owns the only link into All Holdings (PG-2758).
  const hasAllHoldings = (allHoldingsData?.data || []).length > 0;

  const handleShowMore = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (error) {
    dispatch(
      toast({
        type: "error",
        body: parseErrorMessage(error),
      })
    );
  }

  if (allHoldingsError) {
    dispatch(
      toast({
        type: "error",
        body: parseErrorMessage(allHoldingsError),
      })
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Loader />
        </CardContent>
      </Card>
    );
  }

  // Nothing on the card and nothing behind it -- hide it entirely.
  if (!holdings.length && !isAllHoldingsLoading && !hasAllHoldings) {
    return null;
  }

  return (
    <>
      <TableSection>
        <Card>
          <CardHeader>
            <Text variant="cardHeading" weight="500" as="h3">
              Top holdings
              <Tooltip
                value={TOP_HOLDINGS_TOOLTIP}
                placement="top"
                tooltipClassName="v2-tooltip"
              >
                <TitleInfoIcon size={14} />
              </Tooltip>
            </Text>
            {hasAllHoldings && (
              <a href="#" onClick={handleShowMore}>
                Show more
              </a>
            )}
          </CardHeader>
          <CardContent>
            <Table
              data={holdings}
              columns={columns}
              isLoading={isLoading}
              notFoundText={
                hasAllHoldings
                  ? "No top holdings for this strategy — use Show more to see all holdings"
                  : "No holdings data available"
              }
              showPagination={false}
            />
          </CardContent>
        </Card>
      </TableSection>

      <AllHoldingsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        holdings={allHoldingsData?.data}
        isLoading={isAllHoldingsLoading}
      />
    </>
  );
};

TopHoldings.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  strategyId: PropTypes.string,
};

export default TopHoldings;
